<mat-sidenav-container class="full-height">
  <mat-sidenav #drawer mode="over" fixedInViewport [attr.role]="(isHandset$ | async) ? 'dialog' : 'navigation'" [opened]="false" class="app-sidenav">
    <div class="sidenav-wrapper">
      <div class="sidenav-header">
        <a href="/" class="logo" [routerLink]="['/']">
          <img src="assets/images/raksainfo.png" alt="RaksaInfo Logo" width="192" height="48">
        </a>
      </div>

      <div class="sidenav-content">
        <mat-nav-list>
          @if (!loginReady) {
            <div class="sidenav-skeleton">
              <div class="sidenav-skeleton-block"></div>
            </div>
          }

          @if (loginReady && (loginState$ | async)) {
            <ri-toolbar-project-selector
              [selectedProject]="selectedProject$ | async"
              [projects]="projects"
              [showLoading]="(projectsLoading$ | async) || false"
              (selectedProjectChange)="changeProject($event)"
              variant="sidenav"
              appFlyIn>
            </ri-toolbar-project-selector>
          }

          @if (loginReady && (loginState$ | async)) {
            <mat-divider></mat-divider>
          }

          @for (link of navLinks; track link) {
            <button mat-button
              [routerLink]="link.path"
              (click)="closeDrawerOnHandset()"
              [routerLinkActive]="'active'"
              [routerLinkActiveOptions]="{exact: true}">
              <span class="sidenav-nav-content">
                <mat-icon>{{ link.icon }}</mat-icon>
                <span>{{ link.label | translate }}</span>
              </span>
            </button>
          }
          <!-- Same gate as the toolbar link and the calendar route itself. -->
          <button mat-button
            *hasPermission="[permissions.UserPermissions.CanSeeCalendar, permissions.CanViewArrivalsList]"
            [routerLink]="['projects', selectedProject$ | async, 'calendar', 'month']"
            (click)="closeDrawerOnHandset()"
            [routerLinkActive]="'active'"
            [routerLinkActiveOptions]="{exact: true}">
            <span class="sidenav-nav-content">
              <mat-icon>calendar_month</mat-icon>
              <span>{{ "Calendar" | translate }}</span>
            </span>
          </button>
        </mat-nav-list>
      </div>

      <div class="sidenav-footer">
        @if (!loginReady) {
          <div class="sidenav-user-skeleton">
            <div class="sidenav-skeleton-block avatar"></div>
            <div class="sidenav-skeleton-block text"></div>
          </div>
        }

        @if (loginReady && (loginState$ | async)) {
          <div class="sidenav-user-section" appFlyIn>
            <button mat-button [matMenuTriggerFor]="userMenu" class="sidenav-user-btn">
              <span class="sidenav-btn-content">
                <span class="sidenav-user-avatar">{{ username.charAt(0).toUpperCase() }}@if (showDebug) {<span class="debug-dot" role="img" [attr.aria-label]="'USER_MENU.DEBUG_ACTIVE' | translate"></span>}</span>
                <span class="sidenav-user-name">{{ username }}</span>
                <mat-icon class="sidenav-chevron">expand_more</mat-icon>
              </span>
            </button>
          </div>
        }
        <!-- Signed in: theme, language and debug are in the user menu. Signed-out visitors have no user menu. -->
        @if (loginReady && !(loginState$ | async)) {
          <div class="sidenav-utility-row">
            <button mat-icon-button [matMenuTriggerFor]="themeMenu" aria-label="Theme Picker" [matTooltip]="'Theme'">
              <mat-icon>palette</mat-icon>
            </button>
            <button mat-icon-button [matMenuTriggerFor]="langMenu" aria-label="Language" [matTooltip]="'Language'">
              <mat-icon class="fi" [ngClass]="langIcon"></mat-icon>
            </button>
          </div>
        }
        @if (!isLoggedIn) {
          <button mat-button (click)="login()" class="login-button">
            <span class="sidenav-nav-content">
              <mat-icon>login</mat-icon>
              <span>{{ "LOGIN" | translate }}</span>
            </span>
          </button>
        }
      </div>
    </div>
  </mat-sidenav>

  <mat-sidenav-content>
    <app-header
      [userMenu]="userMenu"
      [themeMenu]="themeMenu"
      [langMenu]="langMenu"
      [selectedTheme]="selectedTheme"
      [langIcon]="langIcon"
      (menuToggle)="drawer.toggle()">
    </app-header>
    <ri-announcement-banner></ri-announcement-banner>
    <ri-announcement-gate></ri-announcement-gate>
    <div class="main-content">
      @if (!(isHandset$|async)) {
        <mat-toolbar *hasPermission="permissions.CanCreateSummary" class="p-1 mb-2 toolbar">
          <span class="mr-3 ml-1">Laskutuksen pikalinkit </span>
          <div class="toolbar-buttons">
            <a mat-button *hasPermission="[permissions.CanCreateSummary]" [routerLink]="['/billing', 'create']" [routerLinkActive]="'active'"><mat-icon>add</mat-icon> {{"Create summary"|translate}}</a>
            <a mat-button *hasPermission="[permissions.SalaryTypePermissions.CanManageSalaryTypes]" [routerLink]="['/billing', 'public-holidays']" [routerLinkActive]="'active'"><mat-icon>event</mat-icon> {{"PUBLICHOLIDAYS"|translate}}</a>
            <a mat-button *hasPermission="[permissions.CanViewSummary]" [routerLink]="['/billing', 'dash']" [routerLinkActive]="'active'"><mat-icon>list</mat-icon> {{"SUMMARYLIST"|translate}}</a>
            <a mat-button *hasPermission="[permissions.CanSendInvoice, permissions.CanConfirmSummary]" [routerLink]="['/billing', 'late-entries']" [routerLinkActive]="'active'"><mat-icon>schedule_send</mat-icon> {{"BILLING.NAV.LATE_QUEUE"|translate}}</a>
            <a mat-button *hasPermission="[permissions.CanSendInvoice, permissions.CanConfirmSummary]" [routerLink]="['/billing', 'unbilled-hours']" [routerLinkActive]="'active'"><mat-icon>pending_actions</mat-icon> {{"BILLING.NAV.UNBILLED"|translate}}</a>
            <a mat-button *hasPermission="[permissions.CanManageCompanies]" [routerLink]="['/companies']" [routerLinkActive]="'active'"><mat-icon>work</mat-icon> {{"Companies"|translate}}</a>
            <a mat-button *hasPermission="[permissions.CanViewProjects, permissions.CanManageProjects, permissions.CanEditProjects]" [routerLink]="['/projects']" [routerLinkActive]="'active'"><mat-icon>domain</mat-icon> {{"Projects"|translate}}</a>
            <a mat-button *hasPermission="[permissions.CanManageWarehouses, permissions.CanViewOrders]" [routerLink]="['/warehouses','list']" [routerLinkActive]="'active'"><mat-icon>warehouse</mat-icon> {{"Warehouses"|translate}}</a>
            <a mat-button *hasPermission="[permissions.CanManageHours, permissions.CanCheckEntry, permissions.CanAcceptEntry, permissions.CanSeeContractorHours, permissions.CanSeeProjectHours]" [routerLink]="['/worktime','reports']" [routerLinkActive]="'active'"><mat-icon>trending_up</mat-icon> {{"Reports"|translate}}</a>
          </div>
        </mat-toolbar>
      }
      <router-outlet #outlet="outlet"></router-outlet>
    </div>
    <div class="app-footer">
      <div class="footer-content">
        <div class="footer-column">
          <div class="footer-text">LTQ-Rakennus Oy</div>
          <div class="footer-text">Muuntotie 3</div>
          <div class="footer-text">Vantaa, Finland</div>
        </div>
        <div class="footer-column">
          <div class="footer-title">{{ 'FOOTER_COMPLIANCE' | translate }}</div>
          <a class="footer-link" [routerLink]="['/privacy']">{{ 'FOOTER_PRIVACY' | translate }}</a>
          <a class="footer-link" href="#" rel="noopener" (click)="openLicenseDialog()">{{ 'FOOTER_LICENSES' | translate }}</a>
        </div>
        <div class="footer-column">
          <div class="footer-title">{{ 'FOOTER_CONTACT' | translate }}</div>
          <div class="footer-text">{{ 'FOOTER_SUPPORT' | translate }}</div>
          <a class="footer-link" href="mailto:support@ltq.fi">support&#64;ltq.fi</a>
          <a class="footer-link" href="tel:+3584552346">+358 4552346</a>
        </div>
      </div>
      <div class="footer-bottom">
        {{ 'FOOTER.COPYRIGHT' | translate: { year: copyrightYear } }}
        ·
        <button type="button" class="footer-link about-link" (click)="openAboutDialog()">{{ 'FOOTER.ABOUT_LINK' | translate }}</button>
        @if (versionLine$ | async; as versionLine) {
          <div class="text-faint">{{ versionLine }}</div>
        }
      </div>
    </div>
  </mat-sidenav-content>
</mat-sidenav-container>

<mat-menu #userMenu="matMenu">

  <!-- ── Resources ────────────────────────────── -->
  <!-- No permission gate: every signed-in user has an own record to view. -->
  <a mat-menu-item routerLink="/omat-tiedot">
    <mat-icon>person</mat-icon>{{ 'MY_ACCOUNT.TITLE' | translate }}
  </a>
  <a mat-menu-item *hasPermission="permissions.UserManualPermissions.CanViewUserManual" routerLink="/manual">
    <mat-icon>menu_book</mat-icon>{{ 'USER_MANUAL.TITLE' | translate }}
  </a>
  <a mat-menu-item *hasPermission="permissions.AccessTokenPermissions.CanManageAccessTokens" routerLink="/access-tokens">
    <mat-icon>vpn_key</mat-icon>{{ 'ACCESS_TOKENS.TITLE' | translate }}
  </a>
  <a mat-menu-item *hasPermission="permissions.AiAssistantPermissions.CanConnectAiAssistant" routerLink="/ai-assistant">
    <mat-icon>smart_toy</mat-icon>{{ 'AI_ASSISTANT.TITLE' | translate }}
  </a>
  <a mat-menu-item *hasPermission="permissions.KioskAccess" routerLink="/kiosk-devices">
    <mat-icon>tv</mat-icon>{{ 'KIOSK.DEVICES.TITLE' | translate }}
  </a>

  <!-- ── Insights ──────────────────────────────── -->
  <mat-divider *hasPermission="[permissions.CanViewAnalytics, permissions.CanViewProjectAnalytics]"></mat-divider>
  <a mat-menu-item *hasPermission="[permissions.CanViewAnalytics]" routerLink="/analytics">
    <mat-icon>analytics</mat-icon>{{ 'homeCard.analytics' | translate }}
  </a>
  <!-- Gated on what /analytics/project actually accepts, not on CanViewAnalytics. It used to
  show for analytics admins the route refuses, and hide from the users it admits. -->
  <a mat-menu-item *hasPermission="[permissions.CanViewProjectAnalytics]" routerLink="/analytics/project">
    <mat-icon>bar_chart</mat-icon>{{ 'PROJECT_ANALYTICS.TITLE' | translate }}
  </a>

  <!-- ── Admin tools ───────────────────────────── -->
  <mat-divider *hasPermission="[permissions.CanSeeDebugInfo, permissions.UserPermissions.CanManageUserRoles]"></mat-divider>
  <a mat-menu-item *hasPermission="permissions.CanSeeRoadmap" routerLink="/roadmap">
    <mat-icon>map</mat-icon>{{ 'ROADMAP.TITLE' | translate }}
  </a>
  <a mat-menu-item *hasPermission="permissions.UserPermissions.CanManageUserRoles" routerLink="/server-status">
    <mat-icon>dns</mat-icon>{{ 'SERVER_STATUS.TITLE' | translate }}
  </a>

  <!-- ── Preferences ───────────────────────────── -->
  <mat-divider></mat-divider>
  <button mat-menu-item class="user-menu-theme" [matMenuTriggerFor]="themeMenu">
    <mat-icon>palette</mat-icon>{{ 'USER_MENU.THEME' | translate }}
  </button>
  <button mat-menu-item class="user-menu-language" [matMenuTriggerFor]="langMenu">
    <mat-icon>language</mat-icon>{{ 'USER_MENU.LANGUAGE' | translate }}
  </button>
  <!-- Same gate as the toolbar/sidenav toggle it replaces. -->
  <button mat-menu-item class="user-menu-debug" *hasPermission="permissions.CanSeeDebugInfo"
    role="menuitemcheckbox" [attr.aria-checked]="showDebug"
    (click)="toggleDebugMode({ checked: !showDebug })">
    <mat-icon>bug_report</mat-icon>{{ 'USER_MENU.DEBUG' | translate }}
    @if (showDebug) {
      <mat-icon class="user-menu-debug-check" iconPositionEnd>check</mat-icon>
    }
  </button>
  @if (showDebug) {
    <button mat-menu-item (click)="testErrorHandler()">
      <mat-icon>bug_report</mat-icon>{{ 'USER_MENU.TEST_ERROR_HANDLER' | translate }}
    </button>
  }

  <!-- ── Session ───────────────────────────────── -->
  <mat-divider></mat-divider>
  <button mat-menu-item (click)="logout()">
    <mat-icon>logout</mat-icon>{{ 'LOGOUT' | translate }}
  </button>
</mat-menu>

<mat-menu #themeMenu="matMenu" class="user-menu-submenu">
  <button mat-menu-item (click)="onThemeChange('m3-dark')">
    <mat-icon class="menu-check" [class.active]="selectedTheme === 'm3-dark'">check</mat-icon>
    <span>RaksaInfo V4 Dark</span>
  </button>
  <button mat-menu-item (click)="onThemeChange('m3-light')">
    <mat-icon class="menu-check" [class.active]="selectedTheme === 'm3-light'">check</mat-icon>
    <span>RaksaInfo V4 Light</span>
  </button>
</mat-menu>

<mat-menu #langMenu="matMenu" class="user-menu-submenu">
  <button mat-menu-item (click)="switchLanguage('gb')">
    <mat-icon class="menu-check" [class.active]="currentLanguage === 'gb' || currentLanguage === 'en'">check</mat-icon>
    <mat-icon class="fi fi-gb"></mat-icon> {{"English"|translate}}
  </button>
  <button mat-menu-item (click)="switchLanguage('fi')">
    <mat-icon class="menu-check" [class.active]="currentLanguage === 'fi'">check</mat-icon>
    <mat-icon class="fi fi-fi"> </mat-icon>{{"Finnish"|translate}}
  </button>
  <button mat-menu-item (click)="switchLanguage('ee')">
    <mat-icon class="menu-check" [class.active]="currentLanguage === 'ee'">check</mat-icon>
    <mat-icon class="fi fi-ee"> </mat-icon>{{"ESTONIAN"|translate}}
  </button>
</mat-menu>
